import React from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/Button';
import { Badge } from '../components/ui/Badge';
import { Card } from '../components/ui/Card';
import { DiveCard } from '../components/dive/DiveCard';
import { UserIcon, MapPinIcon, CalendarIcon, ClockIcon, ChevronRightIcon, PlusIcon, AnchorIcon, TimerIcon, WatchIcon, ThermometerIcon } from 'lucide-react';
// Import new components
import { MetricCard } from '../components/dashboard/MetricCard';
import { DepthChart } from '../components/dashboard/DepthChart';
import { DiveTypesChart } from '../components/dashboard/DiveTypesChart';
import { DiveConsistencyHeatmap } from '../components/dashboard/DiveConsistencyHeatmap';
export const Dashboard: React.FC = () => {
  // Sample recent dives data
  const recentDives = [{
    id: '1',
    location: 'Great Barrier Reef',
    date: 'June 15, 2023',
    depth: 18,
    duration: 45,
    temperature: 24,
    hasPhotos: true
  }, {
    id: '2',
    location: 'Blue Corner, Palau',
    date: 'May 22, 2023',
    depth: 30,
    duration: 52,
    temperature: 27,
    hasPhotos: true
  }, {
    id: '3',
    location: 'Molokini Crater, Maui',
    date: 'April 10, 2023',
    depth: 15,
    duration: 38,
    temperature: 25,
    hasPhotos: false
  }];
  // Sample upcoming events data
  const upcomingEvents = [{
    id: '1',
    title: 'Great Barrier Reef Cleanup',
    type: 'Conservation',
    location: 'Cairns, Australia',
    date: 'July 15, 2023',
    time: '9:00 AM',
    participants: 27
  }, {
    id: '2',
    title: 'Night Diving Workshop',
    type: 'Workshop',
    location: 'Sydney Harbour, Australia',
    date: 'July 22, 2023',
    time: '7:30 PM',
    participants: 15
  }, {
    id: '3',
    title: 'Advanced Open Water Course',
    type: 'Training',
    location: 'Gold Coast, Australia',
    date: 'August 5-7, 2023',
    time: '8:00 AM',
    participants: 8
  }];
  // Sample data for dive statistics
  const depthProgressionData = [{
    date: 'Jan',
    depth: 12
  }, {
    date: 'Feb',
    depth: 15
  }, {
    date: 'Mar',
    depth: 18
  }, {
    date: 'Apr',
    depth: 22
  }, {
    date: 'May',
    depth: 25
  }, {
    date: 'Jun',
    depth: 30
  }, {
    date: 'Jul',
    depth: 32
  }];
  const diveTypesData = [{
    name: 'Reef',
    value: 42,
    color: '#0066CC'
  }, {
    name: 'Wreck',
    value: 15,
    color: '#FF6B35'
  }, {
    name: 'Night',
    value: 8,
    color: '#00BFA5'
  }, {
    name: 'Cave',
    value: 4,
    color: '#6772E5'
  }, {
    name: 'Wall',
    value: 17,
    color: '#FFB400'
  }];
  const diveConsistencyData = [{
    month: 'Jan',
    count: 5
  }, {
    month: 'Feb',
    count: 3
  }, {
    month: 'Mar',
    count: 7
  }, {
    month: 'Apr',
    count: 2
  }, {
    month: 'May',
    count: 8
  }, {
    month: 'Jun',
    count: 4
  }, {
    month: 'Jul',
    count: 6
  }, {
    month: 'Aug',
    count: 0
  }, {
    month: 'Sep',
    count: 3
  }, {
    month: 'Oct',
    count: 9
  }, {
    month: 'Nov',
    count: 5
  }, {
    month: 'Dec',
    count: 2
  }];
  return <div data-id="element-856">
      <div className="flex justify-between items-center mb-5" data-id="element-857">
        <h1 className="text-xl sm:text-2xl font-bold text-navy" data-id="element-858">Dashboard</h1>
        <Link to="/profile" data-id="element-859">
          <Button variant="primary" size="sm" icon={<UserIcon size={16} data-id="element-861" />} data-id="element-860">
            Profile
          </Button>
        </Link>
      </div>
      {/* Dive Statistics Section */}
      <div className="mb-6" data-id="element-862">
        <h2 className="text-lg font-semibold mb-3" data-id="element-863">Dive Statistics</h2>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 md:gap-4 mb-4" data-id="element-864">
          <MetricCard title="Total Dives" value="86" icon={<AnchorIcon size={18} data-id="element-866" />} data-id="element-865" />
          <MetricCard title="Average Depth" value="22m" icon={<AnchorIcon size={18} data-id="element-868" />} subtitle="Across all dives" data-id="element-867" />
          <MetricCard title="Total Dive Time" value="64h" icon={<ClockIcon size={18} data-id="element-870" />} subtitle="Underwater" data-id="element-869" />
          <MetricCard title="Longest Dive" value="72min" icon={<TimerIcon size={18} data-id="element-872" />} subtitle="Personal best" data-id="element-871" />
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4" data-id="element-873">
          <DepthChart data={depthProgressionData} data-id="element-874" />
          <DiveTypesChart data={diveTypesData} data-id="element-875" />
        </div>
        <DiveConsistencyHeatmap data={diveConsistencyData} data-id="element-876" />
      </div>
      <div className="grid grid-cols-1 gap-5 md:grid-cols-2 md:gap-6" data-id="element-877">
        <Card data-id="element-878">
          <div className="flex items-center justify-between mb-4" data-id="element-879">
            <h2 className="text-lg font-semibold" data-id="element-880">Recent Dives</h2>
            <Link to="/dives" data-id="element-881">
              <Button variant="ghost" size="sm" icon={<ChevronRightIcon size={16} data-id="element-883" />} data-id="element-882">
                View All
              </Button>
            </Link>
          </div>
          {recentDives.length > 0 ? <div className="space-y-3" data-id="element-884">
              {recentDives.map(dive => <Link key={dive.id} to={`/dives/${dive.id}`} className="block" data-id="element-885">
                  <div className="p-3 border rounded-lg hover:bg-gray-50 transition-colors" data-id="element-886">
                    <div className="flex justify-between items-start" data-id="element-887">
                      <div className="flex-1 min-w-0" data-id="element-888">
                        <h3 className="font-semibold text-navy truncate" data-id="element-889">
                          {dive.location}
                        </h3>
                        <p className="text-sm text-gray-500" data-id="element-890">{dive.date}</p>
                      </div>
                      {dive.hasPhotos && <Badge variant="secondary" className="ml-2 flex-shrink-0" data-id="element-891">
                          Photos
                        </Badge>}
                    </div>
                    <div className="mt-2 flex flex-wrap gap-2 sm:gap-3" data-id="element-892">
                      <div className="flex items-center text-xs text-gray-600" data-id="element-893">
                        <ClockIcon size={12} className="mr-1 text-primary" data-id="element-894" />
                        <span data-id="element-895">{dive.duration} min</span>
                      </div>
                      <div className="flex items-center text-xs text-gray-600" data-id="element-896">
                        <span data-id="element-897">{dive.depth}m depth</span>
                      </div>
                      <div className="flex items-center text-xs text-gray-600" data-id="element-898">
                        <span data-id="element-899">{dive.temperature}°C</span>
                      </div>
                    </div>
                  </div>
                </Link>)}
            </div> : <p className="text-gray-500" data-id="element-900">No recent dives to display.</p>}
          <div className="mt-4" data-id="element-901">
            <Link to="/dives/new" data-id="element-902">
              <Button variant="outline" size="sm" fullWidth icon={<PlusIcon size={16} data-id="element-904" />} data-id="element-903">
                Log New Dive
              </Button>
            </Link>
          </div>
        </Card>
        <Card data-id="element-905">
          <div className="flex items-center justify-between mb-4" data-id="element-906">
            <h2 className="text-lg font-semibold" data-id="element-907">Upcoming Events</h2>
            <Link to="/community" data-id="element-908">
              <Button variant="ghost" size="sm" icon={<ChevronRightIcon size={16} data-id="element-910" />} data-id="element-909">
                View All
              </Button>
            </Link>
          </div>
          {upcomingEvents.length > 0 ? <div className="space-y-3" data-id="element-911">
              {upcomingEvents.map(event => <div key={event.id} className="p-3 border rounded-lg hover:bg-gray-50 transition-colors" data-id="element-912">
                  <div className="flex justify-between items-start" data-id="element-913">
                    <h3 className="font-semibold truncate mr-2" data-id="element-914">
                      {event.title}
                    </h3>
                    <Badge variant={event.type === 'Conservation' ? 'accent' : event.type === 'Workshop' ? 'primary' : 'secondary'} className="flex-shrink-0" data-id="element-915">
                      {event.type}
                    </Badge>
                  </div>
                  <p className="text-sm text-gray-600 flex items-center mt-1" data-id="element-916">
                    <MapPinIcon size={14} className="mr-1 flex-shrink-0" data-id="element-917" />
                    <span className="truncate" data-id="element-918">{event.location}</span>
                  </p>
                  <p className="text-sm text-gray-600 flex items-center mt-1" data-id="element-919">
                    <CalendarIcon size={14} className="mr-1 flex-shrink-0" data-id="element-920" />
                    <span data-id="element-921">
                      {event.date} • {event.time}
                    </span>
                  </p>
                  <div className="flex items-center gap-1 mt-2" data-id="element-922">
                    <div className="flex -space-x-2" data-id="element-923">
                      <div className="w-6 h-6 rounded-full bg-primary/20 flex items-center justify-center text-primary text-xs" data-id="element-924">
                        +{event.participants}
                      </div>
                    </div>
                    <span className="text-xs text-gray-500" data-id="element-925">participants</span>
                  </div>
                </div>)}
            </div> : <p className="text-gray-500" data-id="element-926">No upcoming events to display.</p>}
          <div className="mt-4" data-id="element-927">
            <Button variant="outline" size="sm" fullWidth data-id="element-928">
              Find More Events
            </Button>
          </div>
        </Card>
      </div>
    </div>;
};